import React from "react";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Info,
  ExternalLink,
} from "lucide-react";
import { CopyButton } from "@/components/common/CopyButton";
import { createExplorerLink } from "@/utils/explorer";

export type ToastStatus = "success" | "error" | "pending" | "info";

export interface BaseToastProps {
  status: ToastStatus;
  title: string;
  message?: string;
  txHash?: string;
  details?: Array<{ label: string; value: string }>;
}

/**
 * BaseToast - Unified toast component for all toast notifications
 *
 * WARNING: Do not use this component directly!
 * Use showToast.* functions from @/lib/toast instead.
 *
 * @internal
 */
export const BaseToast: React.FC<BaseToastProps> = ({
  status,
  title,
  message,
  txHash,
  details,
}) => {
  const icons: Record<ToastStatus, React.ReactNode> = {
    success: <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />,
    error: <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />,
    pending: <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />,
    info: <Info className="h-4 w-4 text-blue-500 flex-shrink-0" />,
  };

  return (
    <div className="space-y-2">
      {/* Title + Icon */}
      <div className="flex items-start gap-2">
        <div className="pt-0.5">{icons[status]}</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold leading-5 break-words">{title}</div>
          {message && (
            <div className="text-sm text-muted-foreground mt-1 break-words">
              {message}
            </div>
          )}
        </div>
      </div>

      {/* Optional details */}
      {details && details.length > 0 && (
        <div className="ml-6 text-sm text-muted-foreground space-y-1">
          {details.map((detail, index) => (
            <div key={index} className="break-words">
              <span className="font-medium">{detail.label}:</span>{" "}
              {detail.value}
            </div>
          ))}
        </div>
      )}

      {/* Auto txHash links */}
      {txHash && (
        <div className="flex items-center gap-2 ml-6">
          <CopyButton text={txHash} />
          <a
            href={createExplorerLink(txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-xs">View on Explorer</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </div>
  );
};
